import React, { createContext, useMemo, useRef } from "react";
import { useEvent } from "react-use-event-hook";

import { message as antdMessage } from "antd";
import { ArgsProps, MessageType } from "antd/es/message/interface";

import { isEqual } from "lodash-es";

type TypedMessage = (
  content: React.ReactNode,
  duration?: number,
  onClose?: ArgsProps["onClose"],
) => MessageType;

type MessageContext = {
  info: TypedMessage;
  success: TypedMessage;
  error: TypedMessage;
  warning: TypedMessage;
  loading: TypedMessage;
  open: (args: ArgsProps) => MessageType;
};

export const MessageContext = createContext<MessageContext>({
  info: () => ({}) as MessageType,
  success: () => ({}) as MessageType,
  error: () => ({}) as MessageType,
  warning: () => ({}) as MessageType,
  loading: () => ({}) as MessageType,
  open: () => ({}) as MessageType,
});

export const MessageContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [messageApi, contextHolder] = antdMessage.useMessage();

  const messagesRef = useRef<Record<string, ArgsProps | null>>({});
  const internalKey = useRef<number>(1);

  const removeKey = (key: string) => {
    messagesRef.current[key] = null;
    messagesRef.current = Object.keys(messagesRef.current).reduce(
      (messages, curKey) => {
        const curMessage = messagesRef.current[curKey];
        return curMessage ? { ...messages, [curKey]: curMessage } : messages;
      },
      {},
    );
  };

  const info = useEvent(
    (
      content: React.ReactNode,
      duration?: number,
      onClose?: ArgsProps["onClose"],
    ) => {
      return open({ content, duration, onClose, type: "info" });
    },
  );

  const success = useEvent(
    (
      content: React.ReactNode,
      duration?: number,
      onClose?: ArgsProps["onClose"],
    ) => {
      return open({ content, duration, onClose, type: "success" });
    },
  );

  const error = useEvent(
    (
      content: React.ReactNode,
      duration?: number,
      onClose?: ArgsProps["onClose"],
    ) => {
      return open({ content, duration, onClose, type: "error" });
    },
  );

  const warning = useEvent(
    (
      content: React.ReactNode,
      duration?: number,
      onClose?: ArgsProps["onClose"],
    ) => {
      return open({ content, duration, onClose, type: "warning" });
    },
  );

  const loading = useEvent(
    (
      content: React.ReactNode,
      duration?: number,
      onClose?: ArgsProps["onClose"],
    ) => {
      return open({ content, duration, onClose, type: "loading" });
    },
  );

  const open = useEvent((args: ArgsProps) => {
    const { key, onClose, ...otherArgs } = args;

    const messagesKeys = Object.keys(messagesRef.current);
    const sameMessageIndex = messagesKeys.findIndex((messageKey) =>
      isEqual(messagesRef.current[messageKey], args),
    );

    if (sameMessageIndex > -1) {
      const oldKey = messagesKeys[sameMessageIndex];
      messageApi.destroy(oldKey);
      removeKey(oldKey);
    }

    let messageKey = key?.toString() || "";
    if (!messageKey) {
      messageKey = `antd-message-${internalKey.current}`;
      internalKey.current += 1;
    }

    const messagePromise = messageApi.open({
      ...otherArgs,
      key: messageKey,
      onClose: () => {
        removeKey(messageKey);
        onClose?.();
      },
    });

    messagesRef.current[messageKey] = args;
    return messagePromise;
  });

  const state = useMemo(
    () => ({
      info,
      success,
      error,
      warning,
      loading,
      open,
    }),
    [info, success, error, warning, loading, open],
  );

  return (
    <MessageContext.Provider value={state}>
      {children}
      {contextHolder}
    </MessageContext.Provider>
  );
};
